import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, CheckCircle, AlertCircle, Send, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export const EmailSettingsPanel = () => {
  const { orgId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  
  const [emailFromAddress, setEmailFromAddress] = useState("");
  const [emailFromName, setEmailFromName] = useState("");
  const [emailReplyTo, setEmailReplyTo] = useState("");
  const [domainVerified, setDomainVerified] = useState(false);

  useEffect(() => {
    if (orgId) loadConfig();
  }, [orgId]);

  const loadConfig = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('email-config', {
        body: { action: 'get' }
      });

      if (error) throw error;
      
      if (data?.config) {
        setEmailFromAddress(data.config.email_from_address || "");
        setEmailFromName(data.config.email_from_name || "");
        setEmailReplyTo(data.config.email_reply_to || "");
        setDomainVerified(data.config.resend_domain_verified || false);
      }
    } catch (error: any) {
      console.error('Error loading email config:', error);
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!emailFromAddress || !emailFromName) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.functions.invoke('email-config', {
        body: {
          action: 'update',
          email_from_address: emailFromAddress,
          email_from_name: emailFromName,
          email_reply_to: emailReplyTo || emailFromAddress
        }
      });

      if (error) throw error;
      toast.success('Email settings saved successfully');
    } catch (error: any) {
      console.error('Error saving email config:', error);
      toast.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    const domain = emailFromAddress.split('@')[1];
    if (!domain) {
      toast.error('Invalid email address');
      return;
    }

    setVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('email-config', {
        body: { action: 'verify_domain', domain }
      });

      if (error) throw error;

      if (data?.verified) {
        setDomainVerified(true);
        toast.success('Domain verified successfully!');
      } else {
        toast.error('Domain not verified yet. Please add DNS records in Resend.');
      }
    } catch (error: any) {
      console.error('Error verifying domain:', error);
      toast.error('Failed to verify domain');
    } finally {
      setVerifying(false);
    }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      const { error } = await supabase.functions.invoke('email-config', {
        body: { action: 'test_email' }
      });

      if (error) throw error;
      toast.success('Test email sent! Check your inbox.');
    } catch (error: any) {
      console.error('Error sending test email:', error);
      toast.error('Failed to send test email: ' + error.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading email settings...</div>;
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-6">
        <Mail className="w-5 h-5 text-primary" />
        <h3 className="font-semibold text-lg">Email Configuration</h3>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="emailFromName">From Name *</Label>
          <Input
            id="emailFromName"
            placeholder="Your Company Name"
            value={emailFromName}
            onChange={(e) => setEmailFromName(e.target.value)}
            className="mt-1.5"
          />
          <p className="text-xs text-muted-foreground mt-1">
            This is how your name will appear in recipients' inboxes
          </p>
        </div>

        <div>
          <Label htmlFor="emailFromAddress">From Email Address *</Label>
          <Input
            id="emailFromAddress"
            type="email"
            placeholder="campaigns@yourdomain.com"
            value={emailFromAddress}
            onChange={(e) => setEmailFromAddress(e.target.value)}
            className="mt-1.5"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Must be from a domain you've verified in Resend
          </p>
        </div>

        <div>
          <Label htmlFor="emailReplyTo">Reply-To Email (Optional)</Label>
          <Input
            id="emailReplyTo"
            type="email"
            placeholder="sales@yourdomain.com"
            value={emailReplyTo}
            onChange={(e) => setEmailReplyTo(e.target.value)}
            className="mt-1.5"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Where replies should be sent (defaults to From address)
          </p>
        </div>

        <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30">
          {domainVerified ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <AlertCircle className="w-5 h-5 text-orange-500" />
          )}
          <div className="flex-1">
            <p className="text-sm font-medium">
              Domain Verification Status
            </p>
            <p className="text-xs text-muted-foreground">
              {domainVerified 
                ? 'Your domain is verified and ready to send emails'
                : 'Domain not verified. Verify in Resend to start sending.'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="flex-1"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
          
          <Button 
            variant="outline" 
            onClick={handleVerifyDomain}
            disabled={verifying || !emailFromAddress}
          >
            {verifying ? 'Checking...' : 'Verify Domain'}
          </Button>

          <Button 
            variant="outline" 
            onClick={handleTestEmail}
            disabled={testing || !domainVerified}
          >
            <Send className="w-4 h-4 mr-2" />
            {testing ? 'Sending...' : 'Test'}
          </Button>
        </div>

        <div className="pt-4 border-t">
          <h4 className="text-sm font-medium mb-2">Need help setting up?</h4>
          <a
            href="https://resend.com/domains"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            Verify your domain in Resend
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </Card>
  );
};
